import { formatDate } from '@angular/common';
import { ChangeDetectionStrategy, Component, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { Store } from '@ngrx/store';
import {
  PageMetaService,
  RoutingService,
  StateWithProcess,
  StateWithUser,
  TranslationService,
  UserIdService
} from '@spartacus/core';
import { Order, OrderHistoryFacade, OrderHistoryList, ReplenishmentOrderHistoryFacade } from '@spartacus/order/root';
import { Observable, Subscription, combineLatest } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { fadeIn, fadeOut, slideIn, slideOut } from '../../../../../../custom/animation/custom-animations';
import { ICON_TYPE } from '../../../../../../custom/icons/icon.model';
import { CustomUserOrderService } from '../../../../core/user/facade/order/custom-user-order.service';
import { OrdersFilter } from '../../../../model/order-filters.model';
import { OrderStatusList } from '../../../../model/order-status-list.model';
import { CustomOrderDetailsService } from '../custom-order-details';


@Component({
  selector: 'cx-order-history',
  templateUrl: './custom-order-history.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [fadeIn, fadeOut, slideIn, slideOut],
  standalone: false
})
export class CustomOrderHistoryComponent implements OnInit, OnDestroy {
  protected subscriptions: Subscription = new Subscription();
  pageTitle: string;
  pageDescription: string;
  sortType: string;
  iconTypes = ICON_TYPE;
  showAdvancedSearch = false;
  userId: string;
  orderDetail$: Observable<Order>;
  orderDetail: Order;
  openOrderDetail: string;
  appliedFilter = false;
  showOrderDetail = false;
  statusList: Observable<OrderStatusList>;
  filtersForm: UntypedFormGroup = this.fb.group({
    statuses: [''],
    orderRequest: [''],
    initDate: [''],
    endDate: ['']
  });
  hasReplenishmentOrder$: Observable<boolean> = this.replenishmentOrderHistoryFacade
    .getReplenishmentOrderDetails()
    .pipe(map((order) => order && Object.keys(order).length !== 0));
  isLoaded$: Observable<boolean> = this.orderHistoryFacade.getOrderHistoryListLoaded();
  private PAGE_SIZE = 20;
  orders$: Observable<OrderHistoryList> = this.orderHistoryFacade
    .getOrderHistoryList(this.PAGE_SIZE)
    .pipe(
      tap((orders: OrderHistoryList) => {
        if (orders.pagination) {
          this.sortType = orders.pagination.sort;
        }
      })
    );

  constructor(
    protected routing: RoutingService,
    protected orderHistoryFacade: OrderHistoryFacade,
    protected translation: TranslationService,
    protected replenishmentOrderHistoryFacade: ReplenishmentOrderHistoryFacade,
    protected userOrderService: CustomUserOrderService,
    protected store: Store<StateWithUser | StateWithProcess<void>>,
    protected userIdService: UserIdService,
    protected orderDetailsService: CustomOrderDetailsService,
    protected fb: UntypedFormBuilder,
    protected pageMetaService: PageMetaService,
  ) {
    this.orderDetail$ = this.orderDetailsService.getOrderDetails();

    this.subscriptions.add(this.orderDetail$.subscribe(
      (orderDetail: Order) => {
        this.orderDetail = orderDetail;
      }));

    this.subscriptions.add(
      this.userIdService.takeUserId(true).subscribe(
        () => {
          this.fetchStatusList();
        }
      )
    )
  }

  ngOnInit(): void {
    this.subscriptions.add(
      this.pageMetaService.getMeta().subscribe(
        data => {
          this.pageTitle = data.title;
          this.pageDescription = data.description;
        },
      ),
    );
  }

  changeSortCode(sortCode: string): void {
    const event: { sortCode: string; currentPage: number } = {
      sortCode,
      currentPage: 0,
    };
    this.sortType = sortCode;
    this.fetchOrders(event);
  }

  pageChange(page: number): void {
    const event: { sortCode: string; currentPage: number } = {
      sortCode: this.sortType,
      currentPage: page,
    };
    this.fetchOrders(event);
  }

  getSortLabels(): Observable<{ byDate: string; byOrderNumber: string }> {
    return combineLatest([
      this.translation.translate('sorting.date'),
      this.translation.translate('sorting.orderNumber'),
    ]).pipe(
      map(([textByDate, textByOrderNumber]) => {
        return {
          byDate: textByDate,
          byOrderNumber: textByOrderNumber,
        };
      })
    );
  }

  toggleAdvancedSearch(): void {
    this.showAdvancedSearch = !this.showAdvancedSearch;
  }

  loadOrderDetails(orderCode): void {
    if (this.openOrderDetail !== orderCode) {
      this.userOrderService.loadOrderDetails(orderCode);
      this.openOrderDetail = orderCode;
      this.showOrderDetail = true;
    } else {
      this.orderHistoryFacade.clearOrderDetails();
      this.showOrderDetail = false;
      this.openOrderDetail = null;
    }
  }

  updateDatepicker(filterKey: string): void {
    this.filtersForm.get(filterKey)?.updateValueAndValidity();
  }

  applyFilter(): void {
    this.appliedFilter = true;
    this.fetchOrders({ sortCode: null, currentPage: 0 });
    document.getElementById("table-history").scrollIntoView();
  }

  ngOnDestroy(): void {
    this.orderHistoryFacade.clearOrderList();
    if (this.subscriptions) {
      this.subscriptions.unsubscribe();
    }
  }

  patchDateValue(format: string): OrdersFilter {
    const formInitDate = this.filtersForm.get('initDate').value;
    const formEndDate = this.filtersForm.get('endDate').value;
    let initDate;
    let endDate;

    if (formInitDate) {
      initDate = formatDate(formInitDate, format, 'es-ES');
    }

    if (formEndDate) {
      endDate = formatDate(formEndDate, format, 'es-ES');
    }

    return {
      ...this.filtersForm.value,
      initDate,
      endDate
    };
  }

  private fetchOrders(event: { sortCode: string; currentPage: number }): void {
    const formattedForm = this.patchDateValue('dd/MM/yyyy');
    this.userOrderService.loadOrderList(
      this.PAGE_SIZE,
      event.currentPage,
      event.sortCode,
      formattedForm,
    );
    this.patchDateValue('yyyy-MM-dd');
  }

  fetchStatusList(): void {
    this.userOrderService.loadStatusList();
  }

  getStatusList(): Observable<OrderStatusList> {
    return this.userOrderService.getStatusList();
  }

  repeatOrder(orderCode: string): void {
    this.userOrderService.repeatOrder(orderCode);
  }
}
